/**
 * SaleRegisterContainer
 * --
 */
import React from 'react';
import dayjs from 'dayjs';
import SaleRegisterPresenter from './SaleRegisterPresenter';
import { useUserStack } from 'store';
import { useGetMenus } from 'hooks/MenuHooks';
import { useGetUsers } from 'hooks/UserHooks';
import { useGetShopInformations } from 'hooks/ShopInformationHooks';
import { useGetShopMenus } from 'hooks/ShopMenuHooks';
import { useGetProductCategories } from 'hooks/ProductCategoryHooks';
import { useManualCreateOrder, useUpsertOrder } from 'hooks/OrderHooks';
import MessageAlert, { STORE_URL } from 'utils';

const INITIAL_VALUES = {
  //선택
  user_phone: null,
  //필수
  shop_information_id: null,
  product_information: [],
  final_discount: 0,
  final_price: 0,
  order_route: 'APP',
  payment_method: 'CARD',
  type: 'GENERAL',
  //날짜(저장시 INT로 치환)
  created_at: null,
  payment_completion_date: null,
  //주문 번호 관련
  order_number: '',
  payment_serial_number: '',
  payment_info: '',
  //고정데이터
  payment_state: 'COMPLETED',
  order_status: 'COMPLETED',

  //선택(아직 안씀)
  final_reward_point: 0,
  use_point: 0,
  use_jm_pay: 0,
  coupon_info: null,
};
const SaleRegisterContainer = () => {
  const userInfo = useUserStack();
  const searchInput = React.useRef(null);
  const auth = userInfo?.type;
  const defaultShopId = auth !== 'ADMIN' ? userInfo?.shop_information_id : null;
  const options = {
    gcTime: Infinity,
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];
      else return data;
    },
    initialData: [],
  };
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  //NOTE: 기본
  const [isLoading, setIsLoading] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(INITIAL_VALUES);
  //NOTE: 회원
  const [selectedUserPhone, setSelectedUserPhone] = React.useState(null);
  const [userData, setUserData] = React.useState(null);
  //NOTE: 메뉴
  const [selectedList, setSelectedList] = React.useState([]);
  const [amountList, setAmountList] = React.useState([]);
  //NOTE: 모달
  const [modalType, setModalType] = React.useState('MENU');
  const [isOpen, setIsOpen] = React.useState(false);
  //NOTE: 엑셀
  const [uploadData, setUploadData] = React.useState(null);

  /* ===== VARIABLE ===== */
  //NOTE: 주문 생성
  const { createManualOrder } = useManualCreateOrder();
  const { upsertOrder } = useUpsertOrder();
  const mutate = {
    CREATE: createManualOrder,
    UPSERT: upsertOrder,
  };

  // NOTE: 메뉴 조회
  const { data: MenuList } = useGetMenus(
    {
      join_tables: '["product_category","menu_file"]',
    },
    options
  );
  // NOTE: 회원 조회
  const { data: user } = useGetUsers(
    { user_phone: selectedUserPhone },
    {
      ...options,
      enabled: selectedUserPhone !== null,
    }
  );
  // NOTE: 매장 조회
  const { data: ShopList } = useGetShopInformations(
    {
      application_status: 'APPROVE',
      type: 'STORE',
      //매장 관리자 해당 매장만 조회
      ...(auth !== 'ADMIN' && {
        shop_information_id: userInfo?.shop_information_id,
      }),
    },
    options
  );
  // NOTE: 상품 카테고리 데이터 호출
  const { data: Categories } = useGetProductCategories(
    { type: 'MENU' },
    options
  );
  // NOTE: 매장메뉴 조회
  const { data: ShopMenuList } = useGetShopMenus(
    {
      shop_information_id: inputValue?.shop_information_id,
      is_show: 'Y',
    },
    {
      ...options,
      enabled: inputValue?.shop_information_id !== null,
    }
  );
  /**
   * 매장 메뉴 데이터 정제
   * --
   */
  const menuList = React.useMemo(() => {
    return MenuList?.filter((m) =>
      ShopMenuList?.some((sm) => sm?.menu_id === m?.menu_id)
    ).map((m) => {
      const matchedCategory = Categories?.find(
        (c) => c?.product_category_id === m?.product_category_id
      );
      const thumbnail = m?.menu_file?.[0]?.file_url;
      return {
        key: m?.menu_id,
        product_category_name: matchedCategory?.product_category_name,
        ...m,
        thumbnail: `${STORE_URL}/${thumbnail}` || null,
      };
    });
  }, [MenuList, ShopMenuList, Categories]);
  /**
   * 매장 데이터 정제
   * --
   */
  const shopList = React.useMemo(() => {
    return ShopList;
  }, [ShopList]);

  /* ===== FUNCTION ===== */
  /**
   * 스테이트 초기화
   * --
   */
  const handleReset = () => {
    setInputValue({
      ...INITIAL_VALUES,
      shop_information_id: defaultShopId,
    });
    setSelectedList([]);
    setAmountList([]);
    setSelectedUserPhone(null);
    setUserData(null);
    setModalType('MENU');
    setIsOpen(false);
  };

  /**
   * 데이터 바인딩 함수
   * @param {string} t
   * @param {any} v
   * @param {function} callback
   * --
   */
  const handleChange = (t, v, callback = null) => {
    let newData = { ...inputValue };

    newData[t] = v;
    if (t === 'shop_information_id') {
      newData.product_information = [];
    }
    const d = {
      ...newData,
    };
    setInputValue(d);

    if (callback) callback(v);
  };
  /**
   * 금액 데이터 바인딩 함수
   * --
   */
  const handleAmountChange = (
    t,
    value,
    key,
    option_id = null,
    option_key = null,
    callback = null
  ) => {
    const updatedList = amountList.map((item) => {
      if (item.key !== key) return item;

      if (t === 'menu_option' && option_id !== null && option_key !== null) {
        const updatedOptions = item.menu_option.map((opt) => {
          if (opt.option_id !== option_id) return opt;

          const updatedInnerOptions = opt.option.map((inner) =>
            inner.option_key === option_key ? { ...inner, count: value } : inner
          );

          return {
            ...opt,
            option: updatedInnerOptions,
          };
        });

        return {
          ...item,
          menu_option: updatedOptions,
        };
      }

      if (t === 'num') {
        return {
          ...item,
          num: value,
        };
      }
      if (t === 'temperature_option') {
        return {
          ...item,
          temperature_option: value,
        };
      }
      return item;
    });

    setAmountList(updatedList);
    if (callback) callback(value);
  };
  /**
   * CHOICE 옵션 사용 여부 토글
   * @param {number | string} key
   * @param {number} option_id
   * @param {boolean} enabled
   * --
   */
  const handleOptionToggle = (key, option_id, enabled) => {
    const updatedList = amountList.map((item) => {
      if (item.key !== key) return item;

      const updatedOptions = item.menu_option.map((opt) =>
        opt.option_id === option_id ? { ...opt, enabled } : opt
      );

      return {
        ...item,
        menu_option: updatedOptions,
      };
    });

    setAmountList(updatedList);
  };
  /**
   * CHOICE 옵션 중 선택
   * @param {number | string} key
   * @param {number} option_id
   * @param {number | string} selected_key
   * --
   */
  const handleChoiceSelect = (key, option_id, selected_key) => {
    const updatedList = amountList.map((item) => {
      if (item.key !== key) return item;

      const updatedOptions = item.menu_option.map((opt) => {
        if (opt.option_id !== option_id) return opt;

        const updatedInner = opt.option.map((o) => ({
          ...o,
          default: o.option_key === selected_key,
        }));

        return { ...opt, option: updatedInner };
      });

      return {
        ...item,
        menu_option: updatedOptions,
      };
    });

    setAmountList(updatedList);
  };
  /**
   * 상품 정보 추가 함수
   * --
   */
  const handleMenuComplete = () => {
    try {
      if (!amountList.length) {
        return MessageAlert.info('선택된 메뉴가 없습니다.');
      }
      if (amountList?.some((menu) => menu.temperature_option === 'ALL')) {
        return MessageAlert.info('온도를 선택 해주세요.');
      }

      const cleanedList = amountList.map((menu) => {
        let totalOptionPrice = 0;

        const cleanedOptions = menu.menu_option
          .map((opt) => {
            // count가 있는 옵션만 필터링
            const filteredOption = opt.option
              .filter((inner) => {
                // CHOICE 타입일 경우 default 이거나 count > 0인 것만
                if (opt.option_type === 'CHOICE') {
                  return inner.default;
                }
                // COUNT 타입일 경우 count > 0만
                return inner.count > 0;
              })
              .map((item) => ({
                ...item,
                count: item?.count || 1,
              }));

            if (!filteredOption.length) return null;

            // 옵션 가격 합산
            filteredOption.forEach((inner) => {
              totalOptionPrice += inner.option_price * (inner.count || 0);
            });

            return {
              // ...opt,
              option_title: opt?.option_title,
              option: filteredOption,
            };
          })
          .filter(Boolean); // null 제거

        const totalPrice = (menu.price + totalOptionPrice) * menu.num;
        return {
          ...menu,
          menu_option: cleanedOptions,
          total_price: totalPrice,
          total_option_price: totalOptionPrice,
        };
      });
      setInputValue({
        ...inputValue,
        product_information: cleanedList,
        final_price: cleanedList.reduce((acc, cur) => acc + cur.total_price, 0),
      });
      setIsOpen(false);
      setModalType('MENU');
    } catch (e) {
      return MessageAlert.info('오류가 발생 했습니다.');
    } finally {
      return;
    }
  };

  /**
   * 회원 선택 핸들러
   * --
   * @param {string} type
   * @param {object | array} obj
   */
  const handleSelect = (type, obj) => {
    const selectType = type.toLocaleUpperCase();
    if (selectType === 'RESET') {
      setSelectedList([]);
      return;
    }
    let object = null;
    if (Array.isArray(obj)) {
      object = obj?.map((ob) => ({
        key: ob.key,
        product_category_id: ob.product_category_id,
        menu_id: ob.menu_id,
        menu_name: ob.menu_name,
        price: ob.price,
        num: 1,
        menu_option: ob.menu_option,
        thumbnail: ob?.thumbnail,
        temperature_option: ob?.temperature_option,
        eared_temperature_option: ob?.temperature_option,
      }));
    } else {
      object = {
        key: obj.key,
        product_category_id: obj.product_category_id,
        menu_id: obj.menu_id,
        menu_name: obj.menu_name,
        price: obj.price,
        num: 1,
        menu_option: obj.menu_option,
        thumbnail: obj?.thumbnail,
        eared_temperature_option: obj?.temperature_option,
      };
    }
    switch (selectType) {
      case 'CHECKED': // 개별 선택
        setSelectedList((prev) => [...prev, object]);
        return;
      case 'UNCHECKED': // 개별 선택 해제
        setSelectedList((prev) =>
          prev.filter((select) => select.key !== object.key)
        );
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        setSelectedList(object);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        const removeList = selectedList.filter((user) =>
          object.some((ob) => ob.key === user.key)
        );

        setSelectedList(removeList);

        return;
      case 'ALL': // 검색된 회원 전체 선택/해제
        const isAll = selectedList.length === object.length;

        if (!isAll) {
          setSelectedList(object);
        } else {
          setSelectedList([]);
        }
        return;

      default:
        return;
    }
  };

  /**
   * 벨류 체크
   * --
   * @returns
   */
  const handleValueCheck = () => {
    const {
      shop_information_id,
      product_information,
      final_discount,
      final_price,
      created_at,
    } = inputValue;

    if (!shop_information_id) {
      MessageAlert.info('매장을 선택 해주세요.');
      return false;
    }
    if (!product_information.length) {
      MessageAlert.info('메뉴를 선택 해주세요.');
      return false;
    }
    if (final_discount < 0) {
      MessageAlert.info('할인은 0원 이상 입력해주세요.');
      return false;
    }
    if (final_price < 0) {
      MessageAlert.info('주문가격은 0원 이상 입력해주세요.');
      return false;
    }
    if (!created_at) {
      MessageAlert.info('주문날짜을 입력해주세요.');
      return false;
    }
    return true;
  };
  /**
   * 주문 생성
   * --
   * @param {string} type
   * @returns
   */
  const handleSubmit = async (t) => {
    const type = t.toLocaleUpperCase();
    const message = type === 'CREATE' ? '생성' : '연결';
    const confirm = (msg = '') => window.confirm(msg);
    try {
      let data = null;
      switch (type) {
        case 'CREATE':
          if (!handleValueCheck()) return;
          if (!confirm('주문장을 생성하시겠습니까?')) return;
          setIsLoading(true);
          const {
            created_at,
            shop_information_id,
            payment_method,
            final_price,
            final_discount,
            product_information,
            order_route,
          } = inputValue;
          const nowDateUnix = dayjs().unix();
          const paymentSerialNumber = `M-${created_at.format(
            'YYYYMMDD'
          )}-${String(shop_information_id).padStart(2, '0')}-${nowDateUnix}`;
          // console.log(inputValue);
          data = {
            ...inputValue,
            product_information: product_information?.map(
              ({ key, thumbnail, eared_temperature_option, ...rest }) => ({
                ...rest,
                menu_option: rest.menu_option?.length ? rest.menu_option : null,
              })
            ),
            created_at: created_at.unix(),
            payment_completion_date: created_at.unix(),
            order_number: `M${nowDateUnix}`,
            payment_serial_number: paymentSerialNumber,
            payment_info:
              order_route === 'POS'
                ? {
                    sale_date: created_at.format('YYYYMMDD'),
                    type: shop_information_id === 4 ? 'POST' : 'PRE',
                  }
                : null,
            use_jm_pay: payment_method === 'JM_PAY' ? -final_price : 0,
            final_discount: -final_discount,
          };
          break;
        case 'UPSERT':
          if (!confirm('주문장을 연결하시겠습니까?')) return;
          data = uploadData;
          break;

        default:
          return;
      }

      const { status } = await mutate[type](data);

      if (status === 200) {
        MessageAlert.success(`주문장이 ${message} 되었습니다.`);
        handleReset();
        setUploadData(null);
      } else {
        MessageAlert.warning(`주문장 ${message}에 실패하였습니다.`);
      }
      setTimeout(() => {
        setIsLoading(false);
      }, 800);
    } catch (e) {
      setIsLoading(false);
      MessageAlert.warning(`주문장 ${message}에 실패하였습니다..`);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 5000);
    }
  };

  /* ===== HOOKS ===== */
  /** 회원 저장
   * --
   */
  React.useEffect(() => {
    if (user?.length) {
      setUserData(user[0]);
    } else {
      setUserData(null);
    }
  }, [user]);

  /**
   * 초기값 세팅
   * --
   */
  React.useEffect(() => {
    if (auth === 'ADMIN') {
      setInputValue(INITIAL_VALUES);
    } else {
      setInputValue({
        ...INITIAL_VALUES,
        shop_information_id: defaultShopId,
      });
    }
  }, []);
  /* ===== RENDER ===== */
  return (
    <>
      <SaleRegisterPresenter
        auth={auth}
        searchInput={searchInput}
        menuList={menuList}
        userData={userData}
        shopList={shopList}
        categoryList={Categories}
        //함수
        onChange={handleChange}
        onAmountChange={handleAmountChange}
        onOptionToggle={handleOptionToggle}
        onChoiceSelect={handleChoiceSelect}
        onMenuComplete={handleMenuComplete}
        selectedList={selectedList}
        onSelect={handleSelect}
        modalType={modalType}
        onModalType={setModalType}
        isOpen={isOpen}
        onIsOpen={setIsOpen}
        amountList={amountList}
        onAmountList={setAmountList}
        onSubmit={handleSubmit}
        ////
        isLoading={isLoading}
        inputValue={inputValue}
        selectedUserPhone={selectedUserPhone}
        onSelectedUserPhone={setSelectedUserPhone}
        uploadData={uploadData}
        onUploadData={setUploadData}
      />
    </>
  );
};

export default SaleRegisterContainer;
